import { Button as BlazeButton } from 'blaze-ui/Button'
import { Heading } from 'blaze-ui/Heading/Heading'
import { getMediaFiles } from 'components/Design/UploadsTab/getMediaFiles'
import { useUploads } from 'components/Design/UploadsTab/useUploads'
import { SelectedImage } from 'types'
import { collectResults } from 'utils/query'
import Styles from './Uploads.module.scss'
import { useImagePickerContext } from '../ImagePickerContext'
import { Carousel } from 'components/Carousel/Carousel'
import { RecentUploadsProps } from './Types'
import { Text } from 'blaze-ui/Text/Text'
import { Button } from 'blaze-components/WriterTopBar/Button'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { VideoDuration } from 'components/VideoDuration/VideoDuration'
import cx from 'classnames'
import { Pressable } from '@react-aria/interactions'

const COUNT = 10

interface Props {
  handleSelect: (image: SelectedImage) => void
  file: SelectedImage
  size: 'sm' | 'md'
  showSelected: boolean
  selectedImages: SelectedImage[]
}

const UploadedImage = ({ handleSelect, file, size, showSelected, selectedImages }: Props) => (
  <Pressable onPress={() => handleSelect(file)} key={file.id}>
    <div
      className={cx(Styles.recentUploadsItem, Styles[size], {
        [Styles.isSelected]: showSelected && selectedImages.map((image) => image.id).includes(file.id),
      })}
      role="button"
      aria-label={`Insert file ${file.url}`}
    >
      <img src={file.url} alt="" />
      {file.duration && file.duration > 0 && <VideoDuration duration={file.duration} />}
    </div>
  </Pressable>
)

export const RecentUploads = ({
  onOpenAll,
  label,
  size = 'md',
  headingLevel = 4,
  seeAllButtonVariant = 'tertiary',
  showSelected = false,
  customImages,
  displayType = 'carousel',
}: RecentUploadsProps) => {
  const context = useImagePickerContext()
  const { onConfirm, accept, selectedImages } = context
  const uploadsQuery = useUploads()
  const { isMobile } = useResponsiveDesign()
  const uploads = collectResults(uploadsQuery, (data) => data.files)
  const mediaFiles = getMediaFiles(uploads, accept)
  const mediaFilesAsSelectedFiles: SelectedImage[] = mediaFiles
    .map((mf) => ({ ...mf, url: mf.thumbnail_url as string }))
    .slice(0, COUNT)

  let uploadsLabel = label || (isMobile ? 'Use recently uploaded media' : 'Recently Uploaded Media')

  if (!mediaFilesAsSelectedFiles.length) {
    return null
  }

  const handleSelect = (image: SelectedImage) => {
    onConfirm([image], context)
  }

  const headings = {
    sm: (
      <Text variant={isMobile ? 'placeholder' : 'smallList'} color={isMobile ? 'var(--dark-80)' : undefined}>
        {uploadsLabel}
      </Text>
    ),
    md: <Heading level={headingLevel}>{uploadsLabel}</Heading>,
  }

  return (
    <div className={cx(Styles.recentUploads, Styles[size])}>
      <div className={cx(Styles.recentUploadsHeader, Styles[`heading${headingLevel}`])}>
        {isMobile ? (
          <>
            {headings[size]}
            <Button
              onPress={() => {
                // Need to add a timeout so that the click event doesn't bleed through to the buttons on the next screen
                setTimeout(() => onOpenAll(), 50)
              }}
            >
              <Text variant="smallList" color="var(--selected-fill)">
                See All
              </Text>
            </Button>
          </>
        ) : (
          <>
            {headings[size]}
            <BlazeButton variant={seeAllButtonVariant} size="sm" onPress={onOpenAll}>
              See All
            </BlazeButton>
          </>
        )}
      </div>

      {displayType === 'carousel' && (
        <div className={cx(Styles.recentUploadsCarousel, Styles[size])}>
          <Carousel
            withControls
            options={{ slidesToScroll: 3 }}
            offset={isMobile ? 10 : 0}
            slideGap={isMobile ? 2 : 10}
            buttonSize="sm"
          >
            {customImages?.map((file) => (
              <UploadedImage
                key={file.id}
                handleSelect={handleSelect}
                file={file}
                showSelected={showSelected}
                selectedImages={selectedImages}
                size={size}
              />
            ))}
            {mediaFilesAsSelectedFiles
              .filter((mf) => !customImages?.map((i) => i.id).includes(mf.id))
              .map((file) => (
                <UploadedImage
                  key={file.id}
                  handleSelect={handleSelect}
                  file={file}
                  showSelected={showSelected}
                  selectedImages={selectedImages}
                  size={size}
                />
              ))}
          </Carousel>
        </div>
      )}

      {displayType === 'grid' && (
        <div className={cx(Styles.grid, Styles[size])}>
          {customImages?.map((file) => (
            <UploadedImage
              key={file.id}
              handleSelect={handleSelect}
              file={file}
              showSelected={showSelected}
              selectedImages={selectedImages}
              size={size}
            />
          ))}
          {mediaFilesAsSelectedFiles
            .filter((mf) => !customImages?.map((i) => i.id).includes(mf.id))
            .slice(0, 9 - (customImages?.length || 0))
            .map((file) => (
              <UploadedImage
                key={file.id}
                handleSelect={handleSelect}
                file={file}
                showSelected={showSelected}
                selectedImages={selectedImages}
                size={size}
              />
            ))}
        </div>
      )}
    </div>
  )
}
